/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { Table, Button, Modal, Input, Space, Spin } from 'antd';
import { useGetSaveUsersQuery, useDetachSaveUserMutation, useAssignSaveUserRoleMutation } from '../../api/saveUsersApi';
import { FarmUserItem } from '../../models/Farm';
import { toastError } from '../../utils/toast';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';

const styles = {
  container: css({ marginTop: 16 }),
  header: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }),
  headerTitle: css({ margin: 0 }),
};

interface Props {
  saveId: string;
  onInviteClick?: () => void;
}

const UsersSection: React.FC<Props> = ({ saveId, onInviteClick }) => {
  const { t } = useTranslation('saves');
  const { data: users, isLoading, isError } = useGetSaveUsersQuery(saveId);
  const [detachUser] = useDetachSaveUserMutation();
  const [assignRole, { isLoading: assigning }] = useAssignSaveUserRoleMutation();
  const [roleModal, setRoleModal] = useState<{ userId: string; roleCode: string } | null>(null);
  const [modal, contextHolder] = Modal.useModal();

  if (isLoading) return <Spin />;
  // If users cannot be fetched (e.g., no permissions), do not render the block
  if (isError) return null;
  if (!users) return null;

  const noUsers = !users.length;

  const handleDetach = (userId: string) => {
    modal.confirm({
      title: t('users.detachConfirm'),
      okText: t('common:actions.yes'),
      cancelText: t('common:actions.no'),
      onOk: async () => {
        try {
          await detachUser({ saveId, userId }).unwrap();
        } catch (e) {
          toastError(e);
        }
      },
    });
  };

  const handleOpenRole = (user: FarmUserItem) => {
    setRoleModal({ userId: user.user.id, roleCode: user.membership.role.code });
  };

  const handleRoleOk = async () => {
    if (!roleModal) return;
    try {
      await assignRole({ saveId, userId: roleModal.userId, roleCode: roleModal.roleCode }).unwrap();
      setRoleModal(null);
    } catch (e) {
      toastError(e);
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return '';
    // Expecting ISO string; cut date part only without timezone issues
    const idx = value.indexOf('T');
    return idx > 0 ? value.slice(0, idx) : value;
  };

  const columns = [
    {
      title: t('users.columns.name'),
      render: (_: any, record: FarmUserItem) =>
        record.user?.username || record.user?.name || record.user?.email || record.user?.id,
    },
    {
      title: t('users.columns.role'),
      dataIndex: ['membership', 'role', 'code'],
      render: (code: string) => t(`users.roles.${code}`, { defaultValue: code }),
    },
    { title: t('users.columns.addedAt'), dataIndex: ['membership', 'addedAt'], render: (v: string) => formatDate(v) },
    {
      title: t('common:labels.actions'),
      render: (_: any, record: FarmUserItem) => (
        <Space>
          <Button onClick={() => handleOpenRole(record)}>{t('users.changeRole')}</Button>
          <Button danger onClick={() => handleDetach(record.user.id)}>{t('users.detach')}</Button>
        </Space>
      ),
    },
  ];

  return (
    <div css={styles.container}>
      {contextHolder}
      <div css={styles.header}>
        <h3 css={styles.headerTitle}>{t('users.title')}</h3>
        {onInviteClick && (
          <Button type="primary" onClick={onInviteClick}>
            {t('users.inviteUser')}
          </Button>
        )}
      </div>
      {noUsers ? (
        <div>{t('users.empty')}</div>
      ) : (
        <Table rowKey={(u) => u.user.id} dataSource={users} columns={columns} pagination={false} />
      )}
      <Modal
        title={t('users.changeRoleTitle')}
        open={!!roleModal}
        onOk={handleRoleOk}
        confirmLoading={assigning}
        onCancel={() => setRoleModal(null)}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
      >
        <Input
          placeholder={t('users.roleCodePlaceholder')}
          value={roleModal?.roleCode}
          onChange={(e) =>
            setRoleModal((prev) => (prev ? { ...prev, roleCode: e.target.value } : prev))
          }
        />
      </Modal>
    </div>
  );
};

export default UsersSection;
